import { CoreEvent, eventNames } from '@carvana/core-events';
import { SearchSource } from '@carvana/merch-contracts';
import { DispatcherEvent, NavPayloadAction } from '@carvana/merch-native';
import type {
  ErrorContext,
  SearchBarContextProps,
  SuggestionSelectionProps
} from '@carvana/merch-search-bar';
import { useCallback, useContext, useMemo } from 'react';
import SortOption from '~/constants/common/SortOption';
import { apikUrl, assetsCdnUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import {
  useIsMobile,
  useIsNative,
  useIsTablet
} from '~/context/common/DeviceContext';
import { useSavedSearches } from '~/context/common/SavedSearchesContext';
import { useConfiguration } from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import ModalContext from '~/context/srp/ModalContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import { StickySearchContext } from '~/context/srp/StickySearchProvider';
import sendMessageToNative from '~/native/srp/dispatchers/sendMessageToNative';
import decodeCvnaId from '~/utilities/cvnaId';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useSearchBarProps(): SearchBarContextProps {
  const { setAppliedFilters, setSortBy } = useContext(AppliedFiltersContext);
  const { setIsSearchBarFocused, setSearchBarModalOpen } =
    useContext(ModalContext);
  const { setSearchSource, setKeywordSuggestion } = useContext(
    SearchEventsDataContext
  );
  const { isStickySearchBar } = useContext(StickySearchContext);
  const { shouldUseTitleCasing } = useConfiguration();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const savedSearches = useSavedSearches();
  const trackEvent = useTrackEvent();
  const { state = '' } = useUserLocation();
  const isNative = useIsNative();
  const dealershipId = useDealershipId();

  const onCloseModal = useCallback(() => {
    setSearchBarModalOpen(false);
  }, [setSearchBarModalOpen]);

  const onSavedSearchViewAll = useCallback(() => {
    if (isNative) {
      sendMessageToNative(DispatcherEvent.Link, {
        action: NavPayloadAction.ViewSavedSearches
      });
    } else {
      CoreEvent.emit(eventNames.favorites.mfe.drawer.open, {
        tab: 'searches'
      });
    }
  }, [isNative]);

  const onShowModalClick = useCallback(() => {
    setSearchBarModalOpen(true);
  }, [setSearchBarModalOpen]);

  const onSuggestionSelection = useCallback(
    ({ cvnaId, eventData }: SuggestionSelectionProps) => {
      const searchSource = SearchSource[eventData.suggestionSource ?? ''];
      if (searchSource) {
        setSearchSource(searchSource);
      }
      setKeywordSuggestion({
        ...eventData,
        isStickySearchbar: isStickySearchBar
      });
      const decoded = decodeCvnaId(cvnaId);
      setAppliedFilters(decoded?.filters ?? {});
      if (decoded?.sortBy && decoded.sortBy in SortOption) {
        setSortBy(decoded.sortBy);
      }
    },
    [
      isStickySearchBar,
      setAppliedFilters,
      setKeywordSuggestion,
      setSearchSource,
      setSortBy
    ]
  );

  const onError = useCallback((error: Error, context: ErrorContext) => {
    generateErrorLogger(`SearchBar - ${context.location}`, context)(error);
  }, []);

  return useMemo(
    () => ({
      apiBaseUrl: apikUrl,
      cdnBaseUrl: assetsCdnUrl,
      geolocationState: state,
      dealershipId: dealershipId ?? undefined,
      isMobile,
      isTablet,
      onCloseModal,
      onError,
      onFocus: setIsSearchBarFocused,
      onSavedSearchViewAll,
      onShowModalClick,
      onSuggestionSelection,
      refSource: 'srp',
      savedSearches,
      shouldUseTitleCasing,
      showButton: false,
      trackEvent
    }),
    [
      dealershipId,
      isMobile,
      isTablet,
      onCloseModal,
      onError,
      onSavedSearchViewAll,
      onShowModalClick,
      onSuggestionSelection,
      savedSearches,
      setIsSearchBarFocused,
      shouldUseTitleCasing,
      state,
      trackEvent
    ]
  );
}
